import { useRef, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import { ThemedMaximizeIcon } from "assets/CXFIcons/Designer/Toolbox";
import {
  ThemedCurlyBracesIcon,
  ThemedUndoIcon,
  ThemedRedoIcon,
} from "assets/CXFIcons/Designer/ContextActions";
import { ThemedFileLines } from "assets/CXFIcons/Portal/Dashboard";
import { hideContextPanel } from "redux/actions/flowDesigner/editorContextActionCreator";
import { closeTriggerPropertiesPanel } from "redux/actions/flowDesigner/triggersActionCreator";
import {
  setIsTitlebarTriggerListOpen,
  setIsTitlebarExecuteTriggerListOpen
} from "redux/actions/flowDesigner/triggersActionCreator";
import ExecuteFlowButton from "./ExecuteFlowButton";
import TitlebarActionWrapper from "./TitleActionsWrapper";
import TriggerDeleteDialog from "./TitlebarTriggerList/TriggerDeleteDialog";
import Menu from "@mui/material/Menu";
import MenuList from "@mui/material/MenuList";
import { IconButton, ListItemButton, useMediaQuery } from "@mui/material";
import "./styles.css";
import { useFlexLayout } from "../../../../../../context/AppProvider/FlexLayoutContext";
import { FLEX_LAYOUT_COMPONENT } from "constants/flowDesigner";

function TitlebarActions({ allowUndo, allowRedo, handleUndo, handleRedo }) {
  const { t } = useTranslation("flowDesigner");
  const { showTriggerPropertiesPanel } = useSelector((store) => store?.designer?.triggers);
  const { showContextPanel } = useSelector((store) => store?.designer?.editorContext);
  const { showCalendarDialog } = useSelector((store) => store?.designer?.flowExecution);
  const { isTitlebarTriggerListOpen, isTitlebarExecuteTriggerListOpen } = useSelector(
    (store) => store?.designer?.triggers
  );
  const dispatch = useDispatch();
  const btnContextRef = useRef(null);
  const triggersListButtonRef = useRef(null);
  const mediaMatches = useMediaQuery("(max-width: 1280px)");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isInteractingWithResizer, setIsInteractingWithResizer] = useState(false);
  const { addLayoutTab } = useFlexLayout();

  const undo = () => {
    if (allowUndo) {
      handleUndo();
    }
  };

  const redo = () => {
    if (allowRedo) {
      handleRedo();
    }
  };
  const allowHideTriggerPropsPanel = (e) => {
    const { target } = e;
    const isMultiselectBadge = checkIfElementIsMultiselect(target);
    const isDropdown = checIfElementDropdown(target);
    let isDateTimePicker = false;

    if (target?.classList?.contains("react-datepicker__time-list-item")) {
      isDateTimePicker = true;
    }
    return (
      !target?.classList?.contains("endDateRemover") &&
      !isDateTimePicker &&
      showTriggerPropertiesPanel &&
      !checkIfContainerContainsElement(".MuiBackdrop-root", target) &&
      !isDropdown &&
      !isMultiselectBadge &&
      !checkIfContainerContainsElement(".trigger-props-panel", target) &&
      !checkIfContainerContainsElement(
        ".titlebar-action-trigger-list-wrapper.triggers-list-menu",
        target
      ) &&
      !checkIfContainerContainsElement("#add-options-menu", target) &&
      !checkIfContainerContainsElement(".credential-management-menu", target) &&
      !checkIfContainerContainsElement(".credential-dialog", target) &&
      !checkIfContainerContainsElement(".discard-dialog", target) &&
      !target.classList?.contains("btn-discard-dialog-cancel") &&
      !target.classList?.contains("btn-cancel-add-credentials") &&
      !target.classList?.contains("btn-edit-comment") &&
      !target.classList?.contains("btn-cancel-edit-comment") &&
      !target.classList?.contains("btn-delete-comment") &&
      !target.parentNode?.classList?.contains("comments-input") &&
      !target.parentNode?.classList?.contains("comment-editor")
    );
  };

  const allowHideTriggerList = (e) => {
    const { target } = e;
    return (
      isTitlebarTriggerListOpen &&
      !checkIfContainerContainsElement(".titlebar-action-trigger-list-wrapper", target) &&
      !triggersListButtonRef.current?.contains(target)
    );
  };

  const allowHideExecuteTriggerList = (e) => {
    return (
      isTitlebarExecuteTriggerListOpen &&
      !checkIfContainerContainsElement(".titlebar-action-trigger-list-wrapper", e.target)
    );
  };

  const checkIfContainerContainsElement = (container, target) => {
    let contains = false;
    document.querySelectorAll(container).forEach((element) => {
      if (element.contains(target)) {
        contains = true;
      }
    });

    return contains;
  };

  const checkIfElementIsMultiselect = (target) => {
    return (
      target?.parentNode?.parentNode?.parentNode?.classList?.contains("css-180npq9") ||
      target?.parentNode?.parentNode?.classList?.contains("css-180npq9") ||
      target?.parentNode?.classList?.toString().indexOf("MultiValueRemove") > -1 ||
      target?.parentNode?.parentNode?.classList?.toString().indexOf("MultiValueRemove") > -1 ||
      target?.parentNode?.parentNode?.parentNode?.classList
        ?.toString()
        .indexOf("MultiValueRemove") > -1
    );
  };

  const checIfElementDropdown = (target) => {
    const id =
      target?.parentNode?.parentNode?.parentNode?.getAttribute?.("id") ||
      target?.parentNode?.parentNode?.getAttribute?.("id") ||
      target?.parentNode?.getAttribute?.("id");

    return id?.indexOf("react-select") > -1;
  };

  const handleLogsTab = (tab) => () => {
    addLayoutTab(tab, `executionReport`, `Logs`);
  };

  const handleContextTab = (tab) => () => {
    addLayoutTab(tab, `editorContext`, `Context`);
  };

  const handleCheckIfComponentShouldHide = (event) => {
    if (allowHideTriggerList(event)) {
      dispatch(setIsTitlebarTriggerListOpen(false));
    }
    if (allowHideExecuteTriggerList(event)) {
      dispatch(setIsTitlebarExecuteTriggerListOpen(false));
    }
    if (!isInteractingWithResizer && allowHideTriggerPropsPanel(event)) {
      dispatch(closeTriggerPropertiesPanel());
    }
  };

  const handleAllowMediaMatches = (event) => {
    const target = event.target;

    if (!target) return;

    const parent = target.parentNode;
    const dataset = target.dataset;

    if (
      parent?.getAttribute?.("id") === "titlebarMenuList" ||
      dataset?.id === "titlebarMedia" ||
      target?.classList?.contains("MuiBackdrop-root MuiBackdrop-invisible")
    )
      return;

    handleCheckIfComponentShouldHide(event);
  };

  const handleMenuOpen = (event) => {
    setIsMenuOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleBodyClick = (e) => {
      if (mediaMatches) {
        return handleAllowMediaMatches(e);
      }

      handleCheckIfComponentShouldHide(e);
    };

    window.addEventListener("click", handleBodyClick);

    return () => {
      window.removeEventListener("click", handleBodyClick);
    };
  }, [
    showContextPanel,
    showCalendarDialog,
    isTitlebarTriggerListOpen,
    isTitlebarExecuteTriggerListOpen,
    showTriggerPropertiesPanel,
    mediaMatches,
    isInteractingWithResizer
  ]);

  useEffect(() => {
    const handleMouseDown = (event) => {
      if (event?.target?.classList?.contains("app-sidebar-resizer")) {
        setIsInteractingWithResizer(true);
      }
    };

    const handleMouseUp = () => {
      if (isInteractingWithResizer) {
        setTimeout(() => {
          setIsInteractingWithResizer(false);
        }, 200);
      }
    };

    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isInteractingWithResizer]);

  useEffect(() => {
    const handleCloseByEscape = (event) => {
      if (event?.key === "Escape") {
        dispatch(hideContextPanel());
      }
    };

    document.body.addEventListener("keydown", handleCloseByEscape);

    return () => document.body.removeEventListener("keydown", handleCloseByEscape);
  }, []);

  if (mediaMatches) {
    return (
      <>
        <IconButton
          data-id="titlebarMedia"
          onClick={handleMenuOpen}
          sx={{
            order: 3
          }}
        >
          <ThemedMaximizeIcon />
        </IconButton>
        <Menu
          data-id="titlebarMedia"
          anchorEl={isMenuOpen}
          anchorReference={null}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
          open={Boolean(isMenuOpen)}
          onClose={handleCloseMenu}
          sx={{
            mt: 1,
            "& .MuiMenu-paper": { minWidth: 230 }
          }}
        >
          <MenuList
            id="titlebarMenuList"
            sx={{
              p: 1,
              color: "var(--main-font-color) !important"
            }}
          >
            <ListItemButton className="titlebar__menu-item" onClick={undo} disabled={!allowUndo}>
              <Box component="span" className="titlebar__menu-icon"><ThemedUndoIcon /></Box>
              Undo
            </ListItemButton>
            <ListItemButton className="titlebar__menu-item" onClick={redo} disabled={!allowRedo}>
              <Box component="span" className="titlebar__menu-icon"><ThemedRedoIcon /></Box>
              Redo
            </ListItemButton>
            <ExecuteFlowButton compact />
            <ListItemButton
              className="titlebar__menu-item"
              onClick={handleLogsTab(FLEX_LAYOUT_COMPONENT.executionReport)}
            >
              <Box component="span" className="titlebar__menu-icon"><ThemedFileLines /></Box>
              Logs
            </ListItemButton>
            <ListItemButton
              className="titlebar__menu-item"
              onClick={handleContextTab(FLEX_LAYOUT_COMPONENT.context)}
            >
              <Box component="span" className="titlebar__menu-icon"><ThemedCurlyBracesIcon /></Box>
              Context
            </ListItemButton>
          </MenuList>
        </Menu>
      </>
    );
  }

  return (
    <Box className="commandbar-wrapper">
      <List component={Stack} direction="row">
        <TitlebarActionWrapper
          nomargin
          title={t("menus.undo")}
          className="btn-undo-flow"
          action={undo}
          disableAction={!allowUndo}
        >
          <ThemedUndoIcon />
        </TitlebarActionWrapper>
        <TitlebarActionWrapper
          title={t("menus.redo")}
          className="btn-redo-flow"
          action={redo}
          disableAction={!allowRedo}
        >
          <ThemedRedoIcon />
        </TitlebarActionWrapper>
      </List>
      <List component={Stack} direction="row" sx={{ marginLeft: "60px" }}>
        <ExecuteFlowButton />
        <TitlebarActionWrapper
          title={t("buttons.logs")}
          action={handleLogsTab(FLEX_LAYOUT_COMPONENT.executionReport)}
          allowText
        >
          <ThemedFileLines sx={{ marginRight: "12px", fontSize: "18px !important" }} />
        </TitlebarActionWrapper>
        <TitlebarActionWrapper
          ref={btnContextRef}
          title={t("buttons.context")}
          action={handleContextTab(FLEX_LAYOUT_COMPONENT.context)}
          allowText
        >
          <ThemedCurlyBracesIcon />
        </TitlebarActionWrapper>
      </List>
      <TriggerDeleteDialog />
    </Box>
  );
}

export default TitlebarActions;
